import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { doctorMappingQueryKey, fetchDoctorMapping, saveDoctorMapping } from "@common/lib/api/bridge";
import {
    choicesToActions,
    DoctorChoice,
    initialChoices,
    initialKeep,
    KeepChoices,
    pinKeepers,
    unresolvedBlocking,
} from "./DoctorMappingTable";

type ErrorResponse = { status?: number; json?: () => Promise<{ error?: string; code?: string }> };

/**
 * EN-997. The query, choices state and save mutation (with its error handling) shared by the
 * wizard step and the Settings modal. Each caller supplies only what happens after a save that
 * leaves no blocking rows (`onResolved`); a save that still leaves some stays here, refreshes the
 * choices and shows the "still need a match" message.
 */
export function useDoctorMappingEditor(
    accountId: string | undefined,
    onResolved: () => void,
    enabled: boolean = !!accountId,
) {
    const queryClient = useQueryClient();
    const query = useQuery({
        queryKey: doctorMappingQueryKey(accountId),
        queryFn: ({ queryKey }) => fetchDoctorMapping(queryKey[1] as string),
        enabled,
    });
    const [choices, setChoices] = useState<Record<string, DoctorChoice> | null>(null);
    const [keep, setKeep] = useState<KeepChoices>({});
    const [error, setError] = useState<string | null>(null);
    /**
     * The account the current choices were seeded from. A clinic switch can change `accountId`
     * without remounting (the Settings modal or the wizard staying up), and the old account's
     * choices must not be saved against the new one's doctors.
     */
    const [seededFor, setSeededFor] = useState<string | undefined>(undefined);

    // Seed the choices once the mapping lands, and re-seed when it lands for a different account —
    // adjusting state during render, not in an effect. A background refetch for the SAME account
    // leaves the clinic's in-progress edits alone. `query.data` is always the current key's, so
    // it belongs to `accountId`.
    if (query.data && (choices === null || seededFor !== accountId)) {
        setChoices(initialChoices(query.data));
        setKeep(initialKeep(query.data));
        setSeededFor(accountId);
        setError(null);
    } else if (query.data && choices) {
        // Pin the keeper a group shows once it forms, also adjusted during render.
        const pinned = pinKeepers(query.data, choices, keep);
        if (pinned !== keep) setKeep(pinned);
    }

    // The account on screen now, for a save that resolves after a clinic switch.
    const currentAccountRef = useRef(accountId);
    /**
     * Set synchronously by `submit`. `save.isPending` (and the button it disables) only updates on
     * the next render, so two clicks in one tick would otherwise send two saves, or resolve twice.
     */
    const submittingRef = useRef(false);
    currentAccountRef.current = accountId;

    const save = useMutation({
        // The account is a mutation variable so a save started for one account and resolving after
        // a switch to another caches its result under its own key and leaves the new screen alone.
        mutationFn: (savedFor: string) =>
            saveDoctorMapping(savedFor, query.data ? choicesToActions(query.data, choices ?? {}, keep) : []),
        onSettled: () => {
            submittingRef.current = false;
        },
        onSuccess: (next, savedFor) => {
            queryClient.setQueryData(doctorMappingQueryKey(savedFor), next);
            // A merge deletes or re-keys doctor rows; the Settings doctors list must not keep the duplicate.
            void queryClient.invalidateQueries({ queryKey: ["doctors", savedFor] });
            if (savedFor !== currentAccountRef.current) return;
            if (next.oldDoctors.some((d) => d.blocking)) {
                setChoices(initialChoices(next));
                setKeep(initialKeep(next));
                setError("Some doctors still need a match.");
                return;
            }
            onResolved();
        },
        onError: async (err: unknown, savedFor) => {
            // A save for a clinic no longer on screen must not put its message on the one that is.
            if (savedFor !== currentAccountRef.current) return;
            const response = (err as { response?: ErrorResponse } | null)?.response;
            const body = response?.json
                ? await response.json().catch(() => ({}) as { error?: string; code?: string })
                : {};
            // The clinic may have switched while the body was being read.
            if (savedFor !== currentAccountRef.current) return;
            if (response?.status === 409 && body.code === "account_changed") {
                setError(body.error ?? "The selected clinic changed; reload and try again.");
            } else if (response?.status === 400 && response.json) {
                setError(body.error ?? "Those choices conflict.");
            } else if (response?.status === 409) {
                setError(`${query.data?.pimsName ?? "Cornerstone"} is syncing right now. Try again in a moment.`);
            } else {
                // Only this branch's error is ambiguous about what the server actually applied: a
                // 400/409 above is a definite "nothing was touched", but the POST otherwise applies
                // actions one by one and stops at the first error, so a failure here may still have
                // merged or archived some rows before it stopped.
                void queryClient.invalidateQueries({ queryKey: ["doctors", savedFor] });
                void queryClient.invalidateQueries({ queryKey: doctorMappingQueryKey(savedFor) });
                Sentry.captureException(err);
                // A 400/409 applied nothing, but anything else may have: the POST applies actions one by
                // one and stops at the first error. Show what the server now has instead of stale choices.
                // Deliberately everything not saved, unsaved "Keep" picks included, comes back as not saved:
                // the table must match the server, and the message tells the clinic to check and retry.
                // Saved keepers are rebuilt from the stored map + fold pairs (initialKeep).
                const fresh = await query.refetch();
                if (savedFor !== currentAccountRef.current) return;
                // A failed refetch resolves (it does not throw) with the cached pre-save data, so check the status.
                if (fresh.isSuccess) {
                    setChoices(initialChoices(fresh.data));
                    setKeep(initialKeep(fresh.data));
                    setError("Couldn't save everything. The table now shows what was saved; check it and try again.");
                } else {
                    setError("Couldn't save. Try again.");
                }
            }
        },
    });

    const blockingLeft = query.data && choices ? unresolvedBlocking(query.data, choices) : [];

    return {
        query,
        choices,
        setChoices,
        keep,
        setKeep,
        error,
        save,
        blockingLeft,
        /**
         * Clears the previous error and starts a save. With nothing chosen (every leftover row is
         * non-blocking and left unset) there is nothing to send, and the server rejects an empty
         * list, so it counts as resolved without a request. Before the mapping and choices load
         * it does nothing, so a premature call never advances without a save.
         */
        submit: () => {
            if (submittingRef.current || !query.data || !choices) return;
            setError(null);
            if (choicesToActions(query.data, choices, keep).length === 0) {
                // Terminal: the caller advances or closes, so the guard is never released here.
                submittingRef.current = true;
                onResolved();
                return;
            }
            if (!accountId) return;
            submittingRef.current = true;
            save.mutate(accountId);
        },
    };
}
